import { ReactNode, createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';

import {
    getRouteOrdersAddItemsCategories,
    getRouteOrdersAddItemsInventory,
    getRouteOrdersAddItemsMarketplace,
} from 'shared/const/router';

/**
 * Which catalog shell we are in (legacy vs add-items) and, for add-items, which tab matches the URL.
 * Row identity and `selectedItems` keys are handled by `getItemUid` / checkbox `selectionKey`, not this module.
 */

/**
 * Whether catalog UI runs under the legacy property-catalog surfaces or the
 * add-items-to-order shell (`/orders/add-items/*`). Set only via provider.
 */
export type CatalogFlow = 'legacy' | 'orders-add-items';

/**
 * Sub-surface within add-items: property catalog (categories), inventory, or RE-OPS marketplace.
 * Derived from `location.pathname` via `getCurrentTabFromPathname` (same prefixes as `routeConfig`).
 */
export type OrdersAddItemsCatalogTab =
    | 'property-catalog'
    | 'inventory'
    | 'marketplace';

export type CatalogIdentity =
    | { flow: 'legacy' }
    | { flow: 'orders-add-items'; tab: OrdersAddItemsCatalogTab };

// Provider and context for the catalog flow

const CatalogFlowContext = createContext<CatalogFlow>('legacy');

interface CatalogIdentityProviderProps {
    value: CatalogFlow;
    children: ReactNode;
}

export const CatalogIdentityProvider = ({
    value,
    children,
}: CatalogIdentityProviderProps) => (
    <CatalogFlowContext.Provider value={value}>
        {children}
    </CatalogFlowContext.Provider>
);

/**
 * Resolves add-items tab from the URL using the same path prefixes as `routeConfig`.
 */
export const getCurrentTabFromPathname = (
    pathname: string,
): OrdersAddItemsCatalogTab => {
    if (pathname.startsWith(getRouteOrdersAddItemsMarketplace())) {
        return 'marketplace';
    }

    if (pathname.startsWith(getRouteOrdersAddItemsInventory())) {
        return 'inventory';
    }

    if (pathname.startsWith(getRouteOrdersAddItemsCategories())) {
        return 'property-catalog';
    }

    return 'property-catalog';
};

// Hooks for pulling context values

export const useCatalogIdentity = (): CatalogIdentity => {
    const flow = useContext(CatalogFlowContext);
    const { pathname } = useLocation();

    if (flow === 'legacy') {
        return { flow: 'legacy' };
    }

    return {
        flow: 'orders-add-items',
        tab: getCurrentTabFromPathname(pathname),
    };
};

export const useIsOrdersAddItemsFlow = (): boolean => {
    const flow = useContext(CatalogFlowContext);
    return flow === 'orders-add-items';
};

export const useIsOrdersAddItemsMarketplace = () => {
    const flow = useContext(CatalogFlowContext);
    const { pathname } = useLocation();
    return (
        flow === 'orders-add-items' &&
        getCurrentTabFromPathname(pathname) === 'marketplace'
    );
};
